import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEvent } from '@/layouts/eventContext';
import { Poster } from '@/components';
import { formatEventDateTime, safeEventLink } from '@/features/events/eventFormatting';

export const EventLanding = () => {
  const { t } = useTranslation();
  const event = useEvent();
  const date = formatEventDateTime(
    event.startsAt,
    event.endsAt,
    event.startTimeLabel,
    event.endTimeLabel,
  );
  const links = (event.externalLinks ?? [])
    .map(safeEventLink)
    .filter((l): l is NonNullable<ReturnType<typeof safeEventLink>> => Boolean(l));
  const open = event.saleState === 'open';

  return (
    <div className="relative z-[2] grid min-w-0 grid-cols-1 items-start gap-10 overflow-hidden px-5 py-10 sm:gap-12 sm:px-12 sm:py-12 lg:grid-cols-[1.4fr_0.95fr]">
      <div className="min-w-0">
        <h1
          className="ev-disp ev-accent-text max-w-full break-words"
          style={{
            fontSize: 'clamp(34px, 8vw, 78px)',
            margin: '4px 0 0',
            overflowWrap: 'break-word',
          }}>
          {event.title}
        </h1>

        <div className="mt-6 flex min-w-0 flex-wrap gap-5 font-[var(--ev-mono)] sm:gap-7">
          {date ? (
            <span
              className="ev-mono min-w-0 max-w-full break-words text-sm tracking-[0.04em]"
              style={{ color: 'var(--ev-ink)' }}>
              <span className="uppercase" style={{ color: 'var(--ev-accent2)' }}>
                {t('when')}
              </span>
              <br />
              {date}
            </span>
          ) : null}
          {event.locationName ?? event.locationAddress ? (
            <span
              className="ev-mono min-w-0 max-w-full break-words text-sm tracking-[0.04em]"
              style={{ color: 'var(--ev-ink)' }}>
              <span className="uppercase" style={{ color: 'var(--ev-accent2)' }}>
                {t('where')}
              </span>
              <br />
              {[event.locationName, event.locationAddress].filter(Boolean).join(' · ')}
            </span>
          ) : null}
        </div>

        <div className="mt-8 sm:mt-10">
          {open ? (
            <Link to="tickets" className="ev-btn ev-btn--lg">
              {t('buy_tickets')} →
            </Link>
          ) : (
            <div className="ev-glass max-w-[460px] p-5">
              <div className="ev-disp" style={{ fontSize: 22 }}>
                {event.saleState === 'soldOut' ? t('sold_out_title') : t('presale_not_open_title')}
              </div>
              <p className="ev-mono mt-2 text-sm" style={{ color: 'var(--ev-muted)' }}>
                {event.saleState === 'soldOut' ? t('sold_out_message') : t('presale_soon_message')}
              </p>
            </div>
          )}
          {links.length ? (
            <div className="mt-3.5 flex flex-wrap gap-3">
              {links.map((link, i) => (
                <a
                  key={`${link.url}-${i}`}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="ev-btn ev-btn--ghost">
                  {link.label} ↗
                </a>
              ))}
            </div>
          ) : null}
        </div>

        {event.description ? (
          <section className="mt-10 max-w-full sm:mt-12 sm:max-w-[540px]">
            <h2
              className="ev-mono text-xs uppercase tracking-[0.14em]"
              style={{ color: 'var(--ev-accent2)' }}>
              {t('event_description_title')}
            </h2>
            <div className="ev-prose mt-3 whitespace-pre-line break-words">{event.description}</div>
          </section>
        ) : null}
      </div>

      <div
        className="relative mx-auto min-w-0 lg:mx-0 lg:justify-self-end"
        style={{ width: '100%', maxWidth: 'min(100%, 420px, calc((100vh - 170px) * 0.75))' }}>
        <Poster
          src={event.posterUrl}
          title={event.title}
          sub={[date, event.locationName].filter(Boolean).join(' · ')}
          label="poster"
        />
      </div>
    </div>
  );
};
